// Operations DTOs — PRD §3.14 HK, §3.15 MNT, §3.18 HND, §3.22 AUD.
import { z } from 'zod';
import { dateSchema, uuidSchema } from './common.js';
export const hkStatusSchema = z.enum(['dirty', 'clean', 'inspected']);
export const taskTypeSchema = z.enum(['departure', 'stayover', 'vacant_dirty', 'manual']);
export const taskStatusSchema = z.enum([
    'pending',
    'in_progress',
    'done',
    'rejected',
    'inspected',
    'cancelled',
    'inspected_skipped',
]);
export const roomHousekeepingSchema = z.object({
    status: hkStatusSchema,
    reason: z.string().trim().max(200).optional(),
});
export const taskCreateSchema = z.object({
    roomId: uuidSchema,
    taskType: taskTypeSchema.default('manual'),
    priority: z.enum(['low', 'normal', 'high']).default('normal'),
    notes: z.string().trim().max(500).optional(),
    assignedTo: uuidSchema.optional(),
});
export const taskAssignSchema = z.object({
    taskIds: z.array(uuidSchema).min(1).max(500),
    userId: uuidSchema.nullable(),
});
export const taskDistributeSchema = z.object({
    userIds: z.array(uuidSchema).min(1).max(50),
    businessDate: dateSchema.optional(),
});
export const taskTransitionSchema = z.object({
    to: z.enum(['in_progress', 'done', 'inspected', 'rejected', 'cancelled']),
    reason: z.string().trim().max(300).optional(),
    dnd: z.boolean().optional(),
    returnLater: z.boolean().optional(),
});
export const taskFlagsSchema = z.object({
    dnd: z.boolean().optional(),
    returnLater: z.boolean().optional(),
    notes: z.string().trim().max(500).optional(),
});
export const discrepancySchema = z.object({
    roomId: uuidSchema,
    observed: z.enum(['occupied', 'vacant', 'sleep_out', 'skip']),
    note: z.string().trim().max(300).optional(),
});
export const discrepancyResolveSchema = z.object({ resolution: z.string().trim().min(2).max(300) });
export const ticketCreateSchema = z
    .object({
    kind: z.enum(['ticket', 'out_of_order', 'out_of_service']).default('ticket'),
    roomId: uuidSchema.optional(),
    location: z.string().trim().max(120).optional(),
    title: z.string().trim().min(2).max(160),
    description: z.string().trim().max(2000).optional(),
    priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
    from: dateSchema.optional(),
    to: dateSchema.optional(),
    reason: z.string().trim().max(300).optional(),
    stayId: uuidSchema.optional(),
    /** REQ-MNT-04: release assigned rooms on the affected reservations in one go */
    unassignAffected: z.boolean().default(false),
})
    .refine((v) => v.kind === 'ticket' || v.roomId, 'room required for out-of-order / out-of-service')
    .refine((v) => v.kind !== 'out_of_order' || (v.from && v.to && v.to > v.from), 'out-of-order needs a date range');
export const ticketPatchSchema = z.object({
    status: z.enum(['open', 'in_progress', 'resolved', 'closed']).optional(),
    assignedTo: uuidSchema.nullable().optional(),
    priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
    description: z.string().trim().max(2000).optional(),
    resolution: z.string().trim().max(500).optional(),
});
export const departmentSchema = z.enum([
    'front_office',
    'housekeeping',
    'finance',
    'maintenance',
    'fnb',
    'management',
]);
export const handoverNoteSchema = z.object({
    department: departmentSchema.optional(),
    kind: z.enum(['info', 'follow_up', 'incident']).default('info'),
    priority: z.enum(['low', 'normal', 'high']).default('normal'),
    body: z.string().trim().min(2).max(2000),
    links: z
        .object({
        reservationId: uuidSchema.optional(),
        stayId: uuidSchema.optional(),
        roomId: uuidSchema.optional(),
        guestId: uuidSchema.optional(),
    })
        .default({}),
});
export const handoverCloseSchema = z.object({ comment: z.string().trim().max(500).optional() });
export const auditQuerySchema = z.object({
    from: dateSchema.optional(),
    to: dateSchema.optional(),
    userId: uuidSchema.optional(),
    entityType: z.string().trim().max(40).optional(),
    entityId: uuidSchema.optional(),
    action: z.string().trim().max(60).optional(),
    sensitive: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
//# sourceMappingURL=operations.js.map